import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Title from "../../../../components/title";
import { PermissionsPage } from "../../../../util/permissionsPage";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { funcionariosService } from "../../../../services/funcionarios";
import { toast } from "react-hot-toast";

export const EditPermissions = () => {
  const { id } = useParams();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [bases, setBases] = useState([]);
  const [lojas, setLojas] = useState([]);
  const [paginas, setPaginas] = useState([]);

  useEffect(() => {
    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPermissions = async () => {
    try {
      setIsLoading(true);
      const response = await funcionariosService.getPermissions(id);
      
      if (response.status === 200) {
        setBases(response.data.bases || []);
        setLojas(response.data.lojas || []);
        setPaginas(response.data.paginas || []);
      }
    } catch (error) {
      toast.error("Erro ao carregar permissões do usuário.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Monta payload conforme documentação: baseAcesso, lojaAcesso, paginas (todos como number)
      const baseAcesso = bases.filter((b) => b.hasPermission).map((b) => Number(b.codBase));
      const lojaAcesso = lojas.filter((l) => l.hasPermission).map((l) => Number(l.codLoja));
      const paginasPayload = paginas.flatMap((menu) =>
        (menu.submenus ?? []).flatMap((sub) =>
          (sub.items ?? []).map((item) => ({
            idPermission: Number(item.idPermission),
            hasPermission: item.hasPermission ? 1 : 0,
          }))
        )
      );

      const payload = {
        baseAcesso,
        lojaAcesso,
        paginas: paginasPayload,
      };

      const response = await funcionariosService.updatePermissions(id, payload);
      const { data } = response;

      if (response.status === 200 && data?.success) {
        toast.success(data.msg || "Permissões atualizadas com sucesso!");
        history.goBack();
      } else {
        toast.error(data?.msg || "Erro ao salvar permissões.");
      }
    } catch (error) {
      const msg = error.response?.data?.msg;
      const errors = error.response?.data?.errors;
      toast.error(msg || "Erro ao salvar permissões.");
      if (errors?.length) console.error("Erros:", errors);
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBaseToggle = (codBase) => {
    setBases((prev) =>
      prev.map((base) =>
        base.codBase === codBase
          ? { ...base, hasPermission: base.hasPermission ? 0 : 1 }
          : base
      )
    );
  };

  const handleLojaToggle = (codLoja) => {
    setLojas((prev) =>
      prev.map((loja) =>
        loja.codLoja === codLoja
          ? { ...loja, hasPermission: loja.hasPermission ? 0 : 1 }
          : loja
      )
    );
  };

  const handlePaginaItemToggle = (menuIndex, submenuIndex, itemId) => {
    setPaginas((prev) => {
      const newPaginas = [...prev];
      const newItems = [...newPaginas[menuIndex].submenus[submenuIndex].items];
      
      const itemIndex = newItems.findIndex(i => i.idPermission === itemId);
      if (itemIndex > -1) {
        newItems[itemIndex] = {
          ...newItems[itemIndex],
          hasPermission: newItems[itemIndex].hasPermission ? 0 : 1
        };
      }
      
      newPaginas[menuIndex].submenus[submenuIndex].items = newItems;
      return newPaginas;
    });
  };

  const handleSubmenuToggle = (menuIndex, submenuIndex) => {
    setPaginas((prev) => {
      const newPaginas = [...prev];
      const submenu = newPaginas[menuIndex].submenus[submenuIndex];
      const items = submenu.items ?? [];
      const hasAnyActive = items.some((i) => i.hasPermission);
      const newValue = hasAnyActive ? 0 : 1;

      newPaginas[menuIndex].submenus[submenuIndex].items = items.map((item) => ({
        ...item,
        hasPermission: newValue,
      }));
      return newPaginas;
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {PermissionsPage("configuracaoAcesso") && (
        <Container maxWidth="xl" sx={{ paddingBottom: "2rem", paddingTop: "1rem" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" marginY={2}>
        <Title title="EDITAR PERMISSÕES" />
        <Box gap={2} display="flex">
          <Button variant="contained" color="inherit" onClick={() => history.goBack()} disabled={isSaving}>
            Voltar
          </Button>
          <Button variant="contained" color="error" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Salvando..." : "Salvar"}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* BASES */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardHeader title="BASES" titleTypographyProps={{ color: "error", variant: "h6" }} />
            <Divider />
            <CardContent>
              <Grid container spacing={1}>
                {bases.map((base) => (
                  <Grid item xs={12} sm={6} md={4} key={base.codBase}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!base.hasPermission}
                          onChange={() => handleBaseToggle(base.codBase)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {base.nomeBase}
                        </Typography>
                      }
                      sx={{ width: "100%", margin: 0 }}
                    />
                  </Grid>
                ))}
                {bases.length === 0 && <Typography variant="body2" color="textSecondary">Nenhuma base encontrada.</Typography>}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* LOJAS */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardHeader title="LOJAS" titleTypographyProps={{ color: "error", variant: "h6" }} />
            <Divider />
            <CardContent>
              <Grid container spacing={1}>
                {lojas.map((loja) => (
                  <Grid item xs={12} sm={6} md={4} key={loja.codLoja}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!loja.hasPermission}
                          onChange={() => handleLojaToggle(loja.codLoja)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {loja.nomeLoja}
                        </Typography>
                      }
                      sx={{ width: "100%", margin: 0 }}
                    />
                  </Grid>
                ))}
                {lojas.length === 0 && <Typography variant="body2" color="textSecondary">Nenhuma loja encontrada.</Typography>}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* PÁGINAS */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader title="PÁGINAS" titleTypographyProps={{ color: "error", variant: "h6" }} />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                {paginas.map((menu, menuIndex) => (
                  <Grid item xs={12} md={4} key={menu.menu}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
                      {menu.menu}
                    </Typography>
                    
                    {menu.submenus?.map((submenu, submenuIndex) => (
                      <Accordion key={submenu.name} variant="outlined" disableGutters sx={{ mb: 1, '&:before': { display: 'none' } }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ backgroundColor: "#f9f9f9", minHeight: 48, "& .MuiAccordionSummary-content": { margin: '10px 0' } }}
                        >
                          <Box display="flex" alignItems="center" gap={2} width="100%">
                            <Typography variant="body2" fontWeight="bold">
                              {submenu.name}
                            </Typography>
                            <Box
                              onClick={(e) => e.stopPropagation()}
                              sx={{ marginLeft: "auto" }}
                            >
                              <Switch
                                size="small"
                                checked={submenu.items?.some((i) => i.hasPermission) ?? false}
                                onChange={() => handleSubmenuToggle(menuIndex, submenuIndex)}
                                color="primary"
                              />
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container direction="column" spacing={0.5}>
                            {submenu.items?.map((item) => (
                              <Grid item key={item.idPermission}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={!!item.hasPermission}
                                      onChange={() => handlePaginaItemToggle(menuIndex, submenuIndex, item.idPermission)}
                                      color="primary"
                                    />
                                  }
                                  label={
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                      {item.fieldName || item.field}
                                    </Typography>
                                  }
                                  sx={{ margin: 0 }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Grid>
                ))}
                {paginas.length === 0 && <Typography variant="body2" color="textSecondary" sx={{ ml: 3 }}>Nenhuma página encontrada.</Typography>}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
      )}
    </>
  );
};
